import React, { useRef } from 'react';
import { XMarkIcon, QrCodeIcon, CameraIcon, CubeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface Props {
  aberto: boolean;
  onFechar: () => void;
  /** Se o parâmetro global do multiplicador está ligado — muda o aviso da seção de multiplicação. */
  multiplicadorAtivo: boolean;
}

/** Trecho de código inline (ex.: `12*código`, `×12`) destacado no meio do texto. */
const Cod: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em] text-tx">{children}</span>
);

const Secao: React.FC<{ icone: React.ReactNode; titulo: string; children: React.ReactNode }> = ({ icone, titulo, children }) => (
  <section>
    <h3 className="flex items-center gap-2 text-sm font-semibold text-tx">
      <span className="text-accent-text">{icone}</span>
      {titulo}
    </h3>
    <div className="mt-1.5 space-y-1.5 pl-7 text-sm text-tx-2">{children}</div>
  </section>
);

/**
 * Guia rápido da Conferência — aberto pelo botão "?" do cabeçalho do workspace. Ensina o fluxo
 * (bipar → conferir → concluir) e, em especial, como usar o multiplicador de leitura (digitado
 * `N*código` e o card ×N no celular). Shell (scrim/focus-trap/Esc/clique-fora) igual aos demais
 * diálogos do módulo; no celular abre coladinho embaixo (bottom-sheet), no desktop centralizado.
 */
const AjudaConferenciaDialog: React.FC<Props> = ({ aberto, onFechar, multiplicadorAtivo }) => {
  const painelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(painelRef, aberto, onFechar);

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-[var(--scrim)]" onClick={onFechar} />
      <div
        ref={painelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Como usar a conferência"
        className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-t-iris-lg border border-line bg-surface shadow-iris-xl sm:rounded-iris-lg"
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-tx">Como usar a conferência</h2>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="inline-flex h-8 w-8 items-center justify-center rounded-iris text-tx-3 hover:bg-surface-2"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-5 overflow-y-auto px-4 py-4">
          <Secao icone={<QrCodeIcon className="h-5 w-5" />} titulo="Conferir a nota">
            <p>Bipe cada produto da nota com o leitor de código de barras ou a câmera — a contagem sobe sozinha.</p>
            <p>O resumo de divergência mostra em tempo real o que ainda falta e o que passou da nota.</p>
            <p>Terminou? Toque em <b className="font-medium text-tx">Concluir</b>. Se algo veio errado (falta, avaria…), declare a pendência ali.</p>
          </Secao>

          <Secao icone={<CameraIcon className="h-5 w-5" />} titulo="Bipar pela câmera">
            <p>Toque no ícone de câmera ao lado do campo de código e aponte o código de barras para a área marcada — cada leitura conta automaticamente.</p>
            <p>No celular, o produto lido aparece num <b className="font-medium text-tx">card logo abaixo da câmera</b>, para você conferir na hora o que somou.</p>
            <p>A <b className="font-medium text-tx">chave de acesso</b> da NF-e também pode ser lida pela câmera na tela de iniciar a conferência.</p>
          </Secao>

          <Secao icone={<CubeIcon className="h-5 w-5" />} titulo="Multiplicar a leitura">
            <p>Recebeu vários volumes iguais (ex.: 12 caixas da mesma trena)? Some tudo numa leitura só.</p>
            {!multiplicadorAtivo && (
              <div className="flex items-start gap-2 rounded-iris border border-warning-border bg-warning-soft p-2.5 text-warning">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Este recurso está <b className="font-medium">desligado</b>. Peça ao administrador para ativar em <b className="font-medium">Configurações → “Conferência — multiplicador na bipagem”</b>.</span>
              </div>
            )}
            <p><b className="font-medium text-tx">No leitor ou digitando:</b> digite <Cod>N*</Cod> antes do código. Ex.: <Cod>12*</Cod> e o código conta <b className="font-medium text-tx">12×</b> a embalagem lida (12 caixas de 6 = 72 peças).</p>
            <p><b className="font-medium text-tx">Na câmera (celular):</b> bipe uma caixa e, no card, toque <Cod>×6</Cod> <Cod>×12</Cod> <Cod>×24</Cod>, ou use <Cod>−</Cod> / <Cod>+</Cod> (cada toque = uma embalagem).</p>
            <p>Passar da quantidade da nota é registrado como <b className="font-medium text-tx">excedente</b> — o número fica âmbar e entra na divergência.</p>
          </Secao>
        </div>

        <footer className="flex flex-shrink-0 justify-end border-t border-line px-4 py-3">
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-9 items-center rounded-iris bg-accent px-4 text-sm font-semibold text-white"
          >
            Entendi
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AjudaConferenciaDialog;
